import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { createMockClient } from './db';

// Storage key (same as in AuthContext)
const AUTH_STORAGE_KEY = 'marryplan_auth';

// Vite environment variables (must be prefixed with VITE_)
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

// Check if we have valid credentials
const isMock = !supabaseUrl || 
  supabaseUrl === 'https://placeholder.supabase.co' || 
  !supabaseAnonKey || 
  supabaseAnonKey === 'placeholder';

type StoredAuthTokens = {
  access_token?: string;
  refresh_token?: string;
  user?: unknown;
};

function readStoredAuthRaw(): StoredAuthTokens | null {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as StoredAuthTokens;
  } catch {
    return null;
  }
}

/** Unix exp z JWT, nebo null při neplatném tokenu. */
function getJwtExpUnix(accessToken: string): number | null {
  try {
    const parts = accessToken.split('.');
    if (parts.length < 2) return null;
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4;
    if (pad) b64 += '='.repeat(4 - pad);
    const payload = JSON.parse(atob(b64)) as { exp?: number };
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

/**
 * Před voláním Supabase (Storage, RLS) po delší době v záložce: access token z login API vyprší (~1 h),
 * Supabase pak hlásí „exp claim timestamp check failed“. Obnovíme session z refresh_token a uložíme do localStorage.
 */
export async function ensureSupabaseAccessTokenFresh(): Promise<void> {
  if (isMock || !supabaseUrl || !supabaseAnonKey) return;
  const auth = readStoredAuthRaw();
  if (!auth?.access_token || !auth.refresh_token) return;
  const exp = getJwtExpUnix(auth.access_token);
  const now = Math.floor(Date.now() / 1000);
  const skewSec = 120;
  if (exp !== null && exp > now + skewSec) return;

  const res = await fetch(`${supabaseUrl}/auth/v1/token?grant_type=refresh_token`, {
    method: 'POST',
    headers: {
      apikey: supabaseAnonKey,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ refresh_token: auth.refresh_token }),
  });
  if (!res.ok) return;
  const data = (await res.json()) as { access_token?: string; refresh_token?: string };
  if (!data.access_token || !data.refresh_token) return;
  const next = {
    ...auth,
    access_token: data.access_token,
    refresh_token: data.refresh_token,
  };
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(next));
  window.dispatchEvent(new CustomEvent('marryplan-auth-tokens-refreshed'));
}

// Helper to get stored access token
function getStoredAccessToken(): string | null {
  const parsed = readStoredAuthRaw();
  return parsed?.access_token && typeof parsed.access_token === 'string' ? parsed.access_token : null;
}

// Create Supabase client that uses stored token for auth
function createAuthenticatedClient(): SupabaseClient {
  const token = getStoredAccessToken();

  // Explicitně přidáváme apikey: i když supabase-js by ji měla doplnit přes
  // fetchWithAuth, v některých scénářích (různé verze, importmap, custom fetch,
  // edge cache) může z requestu vypadnout. Tento set vždy zaručí přítomnost
  // hlavičky `apikey`, jinak Supabase odpovídá: "No API key found in request".
  return createClient(supabaseUrl!, supabaseAnonKey!, {
    global: {
      headers: {
        apikey: supabaseAnonKey!,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    },
    auth: {
      autoRefreshToken: false,
      persistSession: false,
      detectSessionInUrl: false,
    },
  });
}

// If credentials are missing or placeholders, use the In-Memory Mock Client
// Otherwise create an authenticated client
export const supabase: SupabaseClient | ReturnType<typeof createMockClient> = isMock 
  ? createMockClient() 
  : createAuthenticatedClient();

// Helper to get a fresh client with current token (use for important operations)
export function getAuthenticatedSupabase(): SupabaseClient {
  if (isMock) {
    return createMockClient() as unknown as SupabaseClient;
  }
  return createAuthenticatedClient();
}

// Export flag to check if we're using real Supabase
export const isUsingRealSupabase = !isMock;

if (isMock) {
  console.log('🔶 MarryPlan running in DEMO mode with Mock DB');
  console.log('💡 To connect to Supabase, set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env');
  if (supabaseUrl && !supabaseAnonKey) {
    console.warn('⚠️ VITE_SUPABASE_URL je nastavena, ale VITE_SUPABASE_ANON_KEY chybí – přepínám do Mock režimu.');
  }
} else {
  console.log('✅ MarryPlan connected to Supabase');
}
